import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import cx from 'classnames';

import Modal from '../../common/Modal';
import ModalAction from '../../common/ModalAction';
import ChordBarsChart from '../../ChordBarsChart';
import ChordBarsTransport, { ChordBarsMixer } from '../../ChordBarsTransport';
import MidiPiano from '../../MidiPiano';
import ChartRun from './ChartRun';
import CountedView from './CountedView';

import { useChartRun } from '../../../context/ChartRunContext';
import useChordBarsPreview from '../../../hooks/useChordBarsPreview';
import useSpacePlay from '../../../hooks/useSpacePlay';
import useSoundingChord from '../../../hooks/useSoundingChord';
import useFontSize from '../../../hooks/useFontSize';
import useBooleanSetting from '../../../hooks/useBooleanSetting';
import useSongRhythm from '../../../hooks/useSongRhythm';
import { hasIntensity, withoutIntensity } from '../../../utils/chartIntensity';
import { BLOCK_CHORDS, toRhythmId } from '../../../utils/chordRhythmPatterns';
import { canSpeak } from '../../../utils/speech';
import { DEFAULT_INSTRUMENT_ID } from '../../../utils/midiInstruments';
import { settingKeys } from '../../../utils/settings';
import { getBeatsPerBar } from '../../../utils/songTiming';

import { ReactComponent as PlayIcon } from '../../../icons/play-solid.svg';
import { ReactComponent as SlidersIcon } from '../../../icons/sliders.svg';
import { ReactComponent as StopIcon } from '../../../icons/square-rounded.svg';

import './ChordBarsPlayerModal.scss';

const ChartLyrics = ({ html, currentChord, currentChordSeconds, countInBeat, beat, timeSignature }) => {
  const content = useMemo(() => parse(html), [html]);
  const ref = useSoundingChord(currentChord, currentChordSeconds, content);
  const fontSize = useFontSize();

  // The class 'song-chart' gives these lyrics View's styling, which is otherwise scoped to #view-body
  return (
    <CountedView countInBeat={countInBeat} beat={beat} timeSignature={timeSignature} entered={currentChord >= 0}>
      <div className="lyrics song-chart" style={{ fontSize }} ref={ref}>
        {content}
      </div>
    </CountedView>
  );
};

ChartLyrics.propTypes = {
  html: PropTypes.string.isRequired,
  currentChord: PropTypes.number.isRequired,
  currentChordSeconds: PropTypes.number.isRequired,
  countInBeat: PropTypes.number.isRequired,
  beat: PropTypes.number.isRequired,
  timeSignature: PropTypes.string.isRequired,
};

const ChordBarsPlayerModal = ({
  isOpen,
  onClose,
  title,
  lines,
  sections,
  repeatCount,
  bpm,
  timeSignature = '4/4',
  instrumentId = DEFAULT_INSTRUMENT_ID,
  melody = null,
  lyricsHtml = '',
  spellChord,
  passes = 1,
  partId = '',
  countInEntry = false,
  category = '',
  songId = '',
  rhythm = null,
  chartEffects = null,
}) => {
  // What plays: this device's pick for the song, over every part; else the part's or the song's
  // rhythm; else block chords
  const { t } = useTranslation();
  const [songRhythm, setSongRhythm] = useSongRhythm(songId);
  const beatsPerBar = getBeatsPerBar(timeSignature);
  const rhythmOf = useCallback(
    (choice) => toRhythmId(songRhythm || choice || BLOCK_CHORDS, beatsPerBar),
    [songRhythm, beatsPerBar],
  );
  const rhythmId = rhythmOf(rhythm);
  const partRhythm = rhythm || BLOCK_CHORDS;
  // Picking what the song already plays is no pick, so a later change to the song still comes through
  const chooseRhythm = (choice) => setSongRhythm(choice === partRhythm ? null : choice);
  const [isLyrics, toggleLyrics] = useBooleanSetting(settingKeys.chordBarsLyrics, false);
  const showLyrics = isLyrics && Boolean(lyricsHtml);

  // The song's charts from this part to its end, as they are when the dialog opens.
  // It is mounted per tap, so each opening reads them afresh.
  const getRunFrom = useChartRun();
  const run = useMemo(
    () => (getRunFrom && partId ? getRunFrom(partId) : { played: [], shown: [] }),
    [getRunFrom, partId],
  );
  const [playThrough, togglePlayThrough] = useBooleanSetting(settingKeys.chordBarsPlayThrough, false);
  const [repeats, toggleRepeats] = useBooleanSetting(settingKeys.chordBarsRepeats, false);
  const [intensityOn, toggleIntensity] = useBooleanSetting(settingKeys.chordBarsIntensity, true);
  const [isMixerOpen, setIsMixerOpen] = useState(false);
  const canPlayThrough = run.played.length > 1;
  const isPlayingThrough = playThrough && canPlayThrough;
  // Intensity reaches the player only through a run, so there is nothing to switch without one
  const hasArrangement = run.played.some(hasIntensity);
  // Each part played through in its own rhythm, unless one has been picked for the whole song. The
  // list shown gets the same copies: ChartRun finds which part is playing by identity.
  const playedRun = useMemo(() => {
    if (!isPlayingThrough) return null;

    const copies = new Map(
      run.played.map((entry) => {
        const played = intensityOn ? entry : withoutIntensity(entry);
        return [entry, { ...played, rhythm: rhythmOf(entry.rhythmChoice) }];
      }),
    );
    return { entries: [...copies.values()], shown: run.shown.map((entry) => copies.get(entry) || entry) };
  }, [isPlayingThrough, run, rhythmOf, intensityOn]);
  const entries = playedRun?.entries || null;
  const repeatsIn = (list) => list.some((entry) => entry.repeatCount >= 2);
  const hasRepeats = repeatsIn(entries || [{ repeatCount: passes }]);
  const offersRepeats = passes >= 2 || (canPlayThrough && repeatsIn(run.played));

  const player = useChordBarsPreview(lines, {
    bpm,
    timeSignature,
    category,
    instrumentId,
    isActive: isOpen,
    rhythm: rhythmId,
    melody,
    repeatCount: passes,
    countInEntry,
    repeats: repeats && hasRepeats,
    entries,
    effects: chartEffects,
  });
  useSpacePlay(player, isOpen);

  const partsPlayed = [...new Set(player.segments.map((segment) => segment.entryIndex))];
  const playingEntry = player.segments[player.currentSegment]?.entryIndex;
  const playing = Math.max(partsPlayed.indexOf(playingEntry), 0) + 1;
  const shownTitle = entries ? t('dialogs.chordBarsPlayer.practice', { playing, total: partsPlayed.length }) : title;

  const transport = (
    <>
      <ModalAction
        icon={player.isPlaying ? <StopIcon className="solid small" /> : <PlayIcon className="solid" />}
        label={player.playLabel}
        active={player.isPlaying}
        busy={player.playBusy}
        progress={player.playProgress}
        disabled={!player.canPlay}
        onClick={player.isPlaying ? player.stop : player.play}
      />
      <ModalAction
        icon={<SlidersIcon />}
        label={t('player.volume')}
        active={isMixerOpen}
        onClick={() => setIsMixerOpen((open) => !open)}
      />
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={shownTitle}
      className={cx('ChordBarsPlayerModal', { 'playing-through': entries })}
      actions={transport}
      sheet={
        <ChordBarsMixer
          isOpen={isMixerOpen}
          onClose={() => setIsMixerOpen(false)}
          player={player}
          idPrefix="chord-bars-player"
          drums
        />
      }
    >
      {entries ? (
        <ChartRun
          entries={entries}
          shown={playedRun.shown}
          segments={player.segments}
          currentSegment={player.currentSegment}
          currentBar={player.currentBar}
          currentChord={player.currentChord}
          currentChordSeconds={player.currentChordSeconds}
          countInBeat={player.countInBeat}
          beat={player.beat}
          timeSignature={timeSignature}
          showIntensity={intensityOn && hasArrangement}
        />
      ) : showLyrics ? (
        <ChartLyrics
          html={lyricsHtml}
          currentChord={player.currentChord}
          currentChordSeconds={player.currentChordSeconds}
          countInBeat={player.countInBeat}
          beat={player.beat}
          timeSignature={timeSignature}
        />
      ) : (
        <ChordBarsChart
          lines={lines}
          sections={sections}
          repeatCount={repeatCount}
          beatsPerBar={getBeatsPerBar(timeSignature)}
          countInEntry={countInEntry}
          currentBar={player.currentBar}
          currentChord={player.currentChord}
          currentChordSeconds={player.currentChordSeconds}
          spellChord={spellChord}
        />
      )}

      {/* Lit by playback only: there is no cursor here, unlike the builder */}
      {player.midiJson && <MidiPiano midiJson={player.midiJson} activeNotes={player.activeNotes} />}

      <ChordBarsTransport
        player={player}
        rhythm={{ choice: songRhythm || partRhythm, onChoose: chooseRhythm, timeSignature }}
        rhythmDefault={partRhythm}
        repeats={
          offersRepeats ? { on: repeats && hasRepeats, onToggle: toggleRepeats, disabled: !hasRepeats } : undefined
        }
        playThrough={canPlayThrough ? { on: playThrough, onToggle: togglePlayThrough } : undefined}
        // Greyed rather than gone when not playing through, the only time intensity plays, or when no
        // part has any set
        intensity={
          canPlayThrough
            ? { on: intensityOn && hasArrangement, onToggle: toggleIntensity, disabled: !entries || !hasArrangement }
            : undefined
        }
        // Greyed rather than gone when not playing through, the only time there are parts to call
        callouts={
          canPlayThrough && canSpeak
            ? { on: player.hasCallouts && player.calloutsOn, onToggle: player.toggleCallouts, disabled: !entries }
            : undefined
        }
        // Greyed while playing through, which shows the whole run as words anyway
        lyrics={
          lyricsHtml
            ? {
              on: showLyrics,
              onToggle: toggleLyrics,
              title: t('dialogs.chordBarsPlayer.followLyrics'),
              disabled: Boolean(entries),
              }
            : undefined
        }
      />
    </Modal>
  );
};

ChordBarsPlayerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  lines: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  sections: PropTypes.arrayOf(PropTypes.number),
  repeatCount: PropTypes.number,
  bpm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  timeSignature: PropTypes.string,
  instrumentId: PropTypes.string,
  melody: PropTypes.shape({
    midiJson: PropTypes.object,
    instrumentId: PropTypes.string,
    semitones: PropTypes.number,
    atBar: PropTypes.number,
  }),
  lyricsHtml: PropTypes.string,
  spellChord: PropTypes.func,
  passes: PropTypes.number,
  partId: PropTypes.string,
  countInEntry: PropTypes.bool,
  category: PropTypes.string,
  /** Whose rhythm a pick here is remembered for */
  songId: PropTypes.string,
  /** The rhythm the part is set to play with, its own or its song's — see getChartRhythm */
  rhythm: PropTypes.string,
  /** The song's reverb and delay, saved on it — see effectSends.js */
  chartEffects: PropTypes.object,
};

export default ChordBarsPlayerModal;
